<script setup lang="ts">
/**
 * Toast — global notification renderer.
 *
 * Subscribes to the toast Pinia store and renders the queue as a
 * fixed-position stack in the bottom-right. Auto-dismiss is driven
 * by each toast's `ttl`; the store handles timer scheduling.
 *
 * Mounted once in App.vue at the root; never instantiated directly.
 */
import { useToastStore, type ToastKind } from '@/stores/toast'
import {
  CheckCircle2,
  Info,
  TriangleAlert,
  XCircle,
  X,
} from 'lucide-vue-next'

const toast = useToastStore()

const ICON_FOR: Record<ToastKind, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  error: XCircle,
}

/** Tailwind class for the icon's text color, per kind. */
const ICON_COLOR_FOR: Record<ToastKind, string> = {
  info: 'text-blue',
  success: 'text-green',
  warning: 'text-amber',
  error: 'text-red',
}
</script>

<template>
  <div
    class="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none"
    role="status"
    aria-live="polite"
  >
    <transition-group name="toast">
      <div
        v-for="t in toast.queue"
        :key="t.id"
        :class="[
          'pointer-events-auto rounded-lg border p-3 shadow-lg',
          'bg-surface',
          'flex items-start gap-3',
        ]"
      >
        <component
          :is="ICON_FOR[t.kind]"
          :size="16"
          :class="['shrink-0 mt-0.5', ICON_COLOR_FOR[t.kind]]"
          aria-hidden="true"
        />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-text-1 m-0 break-words">
            {{ t.message }}
          </p>
          <p
            v-if="t.detail"
            class="mt-0.5 text-xs text-text-3 m-0 break-words"
          >
            {{ t.detail }}
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 text-text-4 hover:text-text-1 transition-colors"
          :aria-label="$t('toast.dismiss')"
          @click="toast.dismiss(t.id)"
        >
          <X :size="14" />
        </button>
      </div>
    </transition-group>
  </div>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 200ms ease-out;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>
